import React, { useEffect } from 'react';
import { RouteProps, withRouter } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import { FaKey, FaUserEdit } from 'react-icons/fa';
import { FiLogOut } from 'react-icons/fi';
import { BiWorld, BiLogIn } from 'react-icons/bi';

import SpeedDial from '@material-ui/lab/SpeedDial';

import styled from 'styled-components';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { MdChevronLeft } from 'react-icons/all';

import { SpeedDialIcon } from '@material-ui/lab';
import { Description } from '@material-ui/icons';
import { useUser } from '../contexts/UserContext';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import { formatSlaveLabel } from '../utils/slaveSelection';
import i18n from '../i18n/i18n';
import { useTheme } from '../contexts/ThemeContext';

const Header = styled.div<HeaderProps>`
  display: flex;
  flex-direction: column;
  background-color: transparent;
  position: relative;
  padding: 12px 0;
`;

const HeaderName = styled.h1<{ hasBackButton?: boolean }>`
  margin-top: 0;
  margin-bottom: 0;
  overflow: hidden;
  white-space: nowrap;
  font-size: 1.6em;
  font-weight: 700;
  min-width: 0;
  max-width: ${(props) => (props.hasBackButton ? '70dvw' : '80dvw')};
`;

const InfoText = styled.div<{ hasBackButton?: boolean }>`
  font-size: 0.8em;
  color: #5e5e5e;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
  max-width: 70dvw;
  margin-top: 2px;
`;

const RowBackContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 64px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  padding-left: 12px;
  color: rgba(77,76,77,255);
  gap: 10px;
`;

const ExtraContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  flex-shrink: 0;
`;

const StyledSpeedDial = styled(SpeedDial)({
  position: 'absolute',
  '&.MuiSpeedDial-directionDown, &.MuiSpeedDial-directionRight': {
    top: 25,
    right: '1px',
  },
  backgroundColor: 'transparent',
  '.MuiFab-primary': {
    backgroundColor: 'transparent',
  },
  '.MuiButtonBase-root.MuiFab-root.MuiSpeedDial-fab.MuiFab-primary': {
    color: 'white',
    boxShadow: 'none',
  },
  '.MuiSpeedDialAction-fab': {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    maxWidth: '40px',
    minHeight: '40px',
    maxHeight: '40px',
  },
  '.MuiSvgIcon-root': {
    fontSize: '2.5rem',
  },
  '.MuiFab-root': {
    width: 'auto',
    height: '40px',
  },
  '#User-actions': {
    marginTop: '-40px',
  },
  '.MuiFab-primary:hover': {
    backgroundColor: 'initial',
  },
});

const StyledDialIcon = styled(AccountCircleIcon)<{ color: string }>((props) => ({
  color: props.color || 'rgba(77,76,77,255)',
  fontSize: '40px',
}));

const StyledCloseIcon = styled(CloseIcon)<{ showVm: boolean, color: string }>((props) => ({
  color: props.showVm
    ? (props.color ? props.color : 'rgb(141,197,62)')
    : (props.color ? props.color : 'rgb(250, 194, 3)'),
}));

const BackButton = styled.button<{ buttonTextColor?: string, color?: string }>`
  width: 35px;
  height: 35px;

  padding: 0;
  border: 0;
  text-decoration: none;

  color: ${(props) => props.buttonTextColor || 'white'};
  background-color: ${(props) => props.color};
  border-radius: 35px;

  opacity: 0.9;
  cursor: pointer;
  outline: none;
  font-size: 2.5rem;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${(props) => props.color};
    opacity: 0.6;
  }

  &:active {
    background-color: ${(props) => props.color};
    opacity: 0.5;
    outline: none;
  }

  &:focus {
    background-color: ${(props) => props.color};
    opacity: 0.4;
    outline: none;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  justify-content: center;
  position: relative;
`;

type HeaderProps = {
  userLogo?: boolean,
  title?: string,
  color?: string,
  showVm?: boolean,
  marginBottom?: string,
  showBackButton?: boolean,
  onBack?: () => void,
  backButtonColor?: string,
  // Overrides the selected-slave label (the EV picker shows the charger it is in)
  slaveLabel?: string,
} & RouteProps

const TopHeader = ({
  userLogo,
  showVm,
  history,
  location,
  title,
  color,
  marginBottom = '0',
  showBackButton,
  onBack,
  backButtonColor,
  showShadow,
  slaveLabel,
}:HeaderProps) => {
  const [open, setOpen] = React.useState(false);
  const { vendingMachineInfo, stickerId } = useVendingMachine();
  const { signOut, loggedIn } = useUser();
  const [slaveInfo, setSlaveInfo] = React.useState<any>(null);
  const shownSlaveInfo = slaveLabel !== undefined ? slaveLabel : slaveInfo;
  const { theme, themeName } = useTheme();
  const iconActionColor = themeName === 'helexia'
    ? '#fbd800'
    : (showVm ? theme.buttonColor || 'rgb(141,197,62)' : theme.buttonColor || 'rgb(250,194,3)');
  const closeIconColor = themeName === 'helexia' ? '#fbd800' : color;
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const [fontSize, setFontSize] = React.useState(26);
  const infoRef = React.useRef<HTMLDivElement>(null);
  const [infoFontSize, setInfoFontSize] = React.useState(13);

  const backgroundColor = showVm ? 'white' : 'transparent';
  const shadow = showVm || showShadow ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none';

  const actions = [
    {
      icon: <BiWorld
        size="25px"
        color={iconActionColor}
        onClick={() => history.push(`/languages${stickerId ? `?s=${stickerId}` : ''}`)}
      />,
      name: i18n.t('languages'),
    },
    {
      icon: <FaUserEdit
        size="25px"
        color={iconActionColor}
        onClick={() => history.push(`/profile${stickerId ? `?s=${stickerId}` : ''}`)}
      />,
      name: i18n.t('name'),
    },
    {
      icon: <FaKey
        size="25px"
        color={iconActionColor}
        onClick={() => history.push(`/password-change${stickerId ? `?s=${stickerId}` : ''}`)}
      />,
      name: i18n.t('password'),
    },
    {
      icon: <Description
        style={{
          color: iconActionColor,
          fontSize: '25px',
        }}
        onClick={() => history.push('/session/documents', { vendingMachineId: vendingMachineInfo?.id })}
      />,
      name: i18n.t('search_documents'),
    },
    {
      icon: <FiLogOut
        size="25px"
        color={iconActionColor}
        onClick={async () => {
          await signOut();
          // With no machine loaded there is nothing left to render anonymously —
          // NoVMView's wallets and history all need a session, so signing out
          // there has to leave the screen. On a machine screen staying put is
          // correct for PUBLIC/ANONYMOUS (anonymous purchase still works);
          // PRIVATE is handled by the redirect effect below.
          if (!vendingMachineInfo) {
            history.push('/');
          }
        }}
      />,
      name: i18n.t('logout'),
    },
  ];

  const loggedOutActions = [
    {
      icon: <BiWorld
        size="25px"
        color={iconActionColor}
        onClick={() => history.push(`/languages${stickerId ? `?s=${stickerId}` : ''}`)}
      />,
      name: i18n.t('languages'),
    },
    {
      icon: <Description
        style={{
          color: iconActionColor,
          fontSize: '25px',
        }}
        onClick={() => history.push('/session/documents', { vendingMachineId: vendingMachineInfo?.id })}
      />,
      name: i18n.t('search_documents'),
    },
    {
      icon: <BiLogIn
        size="25px"
        color={iconActionColor}
        onClick={() => history.push(`/${stickerId ? `?s=${stickerId}` : ''}`)}
      />,
      name: i18n.t('login'),
    },
  ];

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    if (loggedIn || vendingMachineInfo?.loginType !== 'PRIVATE') {
      setOpen(true);
    } else {
      history.push('/');
    }
  };

  const loggedOutUser = vendingMachineInfo?.loginType !== 'ANONYMOUS' ? (
    <StyledSpeedDial
      ariaLabel="User"
      icon={theme.topHeaderIcon ? (
        <SpeedDialIcon icon={(
          <img
            src={theme.topHeaderIcon}
            alt=""
            style={{ width: 40, height: 40, display: 'inline' }}
          />
      )}
        />
      ) : <StyledDialIcon color={color} />}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      direction="down"
    >
      {loggedOutActions.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
          onClick={handleClose}
        />
      ))}
    </StyledSpeedDial>
  ) : null;

  useEffect(() => {
    if (!loggedIn
      && location.pathname !== '/'
      && location.pathname !== '/register'
      && vendingMachineInfo?.loginType === 'PRIVATE') {
      history.push('/');
    }
  }, [loggedIn]);

  useEffect(() => {
    const slaves = vendingMachineInfo?.vendingMachineSlaves || [];
    const selectedSlave = slaves.find((slave) => slave.isSelected);

    setSlaveInfo(selectedSlave
      ? formatSlaveLabel(slaves, selectedSlave, vendingMachineInfo?.identificationNumber)
      : null);
  }, [vendingMachineInfo]);

  const calculateFontSize = (text: string, hasBackButton?: boolean) => {
    const maxFontSize = 26;
    const minFontSize = 16;

    const viewportWidth = window.innerWidth;

    const widthFactor = hasBackButton ? 0.7 : 0.8;
    const containerWidth = viewportWidth * widthFactor;

    const avgCharWidthFactor = 0.55;
    const estimatedFontSize = containerWidth / (text.length * avgCharWidthFactor);

    return Math.max(minFontSize, Math.min(maxFontSize, estimatedFontSize));
  };

  const machineName = vendingMachineInfo?.name || '';

  useEffect(() => {
    if (!titleRef.current) return;

    const element = titleRef.current;

    let size = calculateFontSize(machineName, showBackButton);

    element.style.fontSize = `${size}px`;

    while (element.scrollWidth > element.clientWidth && size > 10) {
      size -= 1;
      element.style.fontSize = `${size}px`;
    }

    setFontSize(size);
  }, [machineName, showBackButton]);

  const calculateInfoFontSize = (text: string) => {
    const maxFontSize = 13;
    const minFontSize = 6;
    const viewportWidth = window.innerWidth;

    const widthFactor = 0.7;
    const containerWidth = viewportWidth * widthFactor;

    const avgCharWidthFactor = 0.55;
    const estimatedFontSize = containerWidth / (text.length * avgCharWidthFactor);

    return Math.max(minFontSize, Math.min(maxFontSize, estimatedFontSize));
  };

  const infoTextValue =
    vendingMachineInfo?.identificationNumber && !vendingMachineInfo?.equipmentType?.label.includes('EV')
      ? `ID ${vendingMachineInfo?.identificationNumber}`
      : shownSlaveInfo || '';

  useEffect(() => {
    if (!infoRef.current) return;

    const element = infoRef.current;

    let size = calculateInfoFontSize(infoTextValue);

    element.style.fontSize = `${size}px`;

    while (element.scrollWidth > element.clientWidth && size > 10) {
      size -= 1;
      element.style.fontSize = `${size}px`;
    }

    setInfoFontSize(size);
  }, [infoTextValue, showBackButton]);

  return (
    <Header style={{backgroundColor, boxShadow: shadow, marginBottom}}>
      <RowBackContainer>
        { showVm ? (
          <ContentContainer style={{color: theme.topHeaderTextColor, zIndex: 5}}>
            <TitleTextContainer>
              <TitleRow>
                {showBackButton && (
                  <BackButton color={backButtonColor} onClick={onBack}>
                    <MdChevronLeft/>
                  </BackButton>
                )}
                <TitleTextContainer >
                  <HeaderName ref={titleRef} style={{fontSize}} hasBackButton={showBackButton}>
                    {machineName}
                  </HeaderName>
                  <InfoText ref={infoRef} style={{fontSize: infoFontSize}}>
                    {vendingMachineInfo?.identificationNumber && !vendingMachineInfo?.equipmentType?.label.includes('EV')
                      ? (<><b>ID</b> {vendingMachineInfo?.identificationNumber}</>)
                      : (vendingMachineInfo?.vendingMachineSlaves && vendingMachineInfo?.equipmentType?.label.includes('EV') && vendingMachineInfo?.vendingMachineSlaves.length > 0 && shownSlaveInfo
                        ? shownSlaveInfo
                        : '')}
                  </InfoText>
                </TitleTextContainer >
              </TitleRow>
            </TitleTextContainer>
          </ContentContainer>
        ) : (
          <ContentContainer>
            <TitleTextContainer>
              <TitleRow>
                {showBackButton && (
                  <BackButton color={backButtonColor} onClick={onBack}>
                    <MdChevronLeft/>
                  </BackButton>
                )}
                <HeaderName
                  style={{
                    marginBottom: '0',
                    color:
                      title === 'initial_menu'
                        ? theme.topHeaderTextColor || 'rgb(255, 255, 255)'
                        : theme.topHeaderTextColor || 'rgba(77,76,77,255)',
                  }}
                >
                  {i18n.t(title)}
                </HeaderName>
                <InfoText style={{fontSize: infoFontSize, minHeight: '1.2em'}}>
                  {/* No infoText for non-VM header, but keep space */}
                </InfoText>
              </TitleRow>
            </TitleTextContainer>
          </ContentContainer>
        )}
        { userLogo && (
          <ExtraContainer>
            {' '}
            {
              (loggedIn
                ? (
                  <div>
                    <StyledSpeedDial
                      ariaLabel="User"
                      icon={(
                        <SpeedDialIcon
                          style={{
                            minWidth: '40px',
                            maxWidth: '40px',
                            minHeight: '40px',
                            maxHeight: '40px',
                          }}
                          icon={
                          theme.topHeaderIcon
                            ? (
                              <img
                                src={theme.topHeaderIcon}
                                alt=""
                                style={{ width: 40, height: 40, display: 'inline' }}
                              />
                            )
                            : <StyledDialIcon color={color} />
                        }
                          openIcon={<StyledCloseIcon showVm={showVm} color={closeIconColor} />}
                        />
                        )}
                      onClose={handleClose}
                      onOpen={handleOpen}
                      open={open}
                      direction="down"
                    >
                      {actions.map((action) => (
                        <SpeedDialAction
                          key={action.name}
                          icon={action.icon}
                          tooltipTitle={action.name}
                          onClick={handleClose}
                        />
                      ))}
                    </StyledSpeedDial>
                  </div>
                )
                : (
                  loggedOutUser
                )

              )
            }
            {' '}
          </ExtraContainer>
        )}
      </RowBackContainer>
    </Header>
  );
};

export default withRouter(TopHeader);
